import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import WorkIcon from '@mui/icons-material/Work';
import EuroIcon from '@mui/icons-material/Euro';
import { adminAPI } from '../../services/api';
import PortalLayout from '../../components/PortalLayout';

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: string;
  chips?: Array<{ label: string; value: string | number; color?: 'default' | 'primary' | 'warning' | 'success' | 'error' | 'info' }>;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color = '#667eea', chips }) => (
  <Card sx={{ height: '100%', borderTop: `3px solid ${color}` }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography color="textSecondary" variant="body2" fontWeight={600}>
          {title}
        </Typography>
        {icon && <Box sx={{ color }}>{icon}</Box>}
      </Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: chips?.length ? 1.5 : 0 }}>
        {value}
      </Typography>
      {chips && chips.length > 0 && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {chips.map((c) => (
              <Chip key={c.label} label={`${c.label}: ${c.value}`} size="small" color={c.color || 'default'} variant="outlined" />
            ))}
          </Stack>
        </>
      )}
    </CardContent>
  </Card>
);

const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const response = await adminAPI.getStatistics();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  const u = stats?.users || {};
  const inq = stats?.inquiries || {};
  const pay = stats?.payments || {};
  const cb = stats?.callbacks || {};
  const jobs = stats?.jobs || {};

  return (
    <PortalLayout>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 4 }}>
          Dashboard
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <StatCard
              title="Nutzer gesamt"
              value={u.total || 0}
              icon={<PeopleIcon />}
              color="#667eea"
              chips={[
                { label: 'Auftraggeber', value: u.customers || 0, color: 'primary' },
                { label: 'Dienstleister', value: u.contractors || 0, color: 'info' },
                { label: 'Team', value: u.staff || 0 },
              ]}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <StatCard
              title="Gesuche"
              value={inq.total || 0}
              icon={<AssignmentIcon />}
              color="#f59e0b"
              chips={[
                { label: 'Ausstehend', value: inq.pending || 0, color: 'warning' },
                { label: 'Genehmigt', value: inq.approved || 0, color: 'success' },
                { label: 'Abgelehnt', value: inq.rejected || 0, color: 'error' },
              ]}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <StatCard
              title="Rückrufanfragen"
              value={cb.total || 0}
              icon={<PhoneCallbackIcon />}
              color="#10b981"
              chips={[
                { label: 'Offen', value: cb.open || 0, color: 'warning' },
                { label: 'Angerufen', value: cb.called || 0, color: 'info' },
                { label: 'Erledigt', value: cb.closed || 0, color: 'success' },
              ]}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <StatCard
              title="Aufträge"
              value={jobs.total || 0}
              icon={<WorkIcon />}
              color="#8b5cf6"
              chips={[
                { label: 'Ausstehend', value: jobs.pending || 0, color: 'warning' },
                { label: 'Veröffentlicht', value: jobs.published || 0, color: 'success' },
                { label: 'Geschlossen', value: jobs.closed || 0 },
              ]}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <StatCard
              title="Gesamteinnahmen"
              value={`€${Number(pay.total_revenue || 0).toFixed(2)}`}
              icon={<EuroIcon />}
              color="#ef4444"
              chips={[
                { label: 'Transaktionen', value: pay.total_payments || 0 },
                { label: 'Abgeschlossen', value: pay.completed_payments || 0, color: 'success' },
              ]}
            />
          </Grid>
        </Grid>
      </Box>
    </PortalLayout>
  );
};

export default AdminDashboardPage;
